import { useLocation } from "react-router";
import BasicCard from "@components/ArticleCard/BasicCard";
import { CardVariations } from "@components/ArticleCard/types";
import { useWindowSize } from "@hooks/useWindowSize";
import { TagType } from "@pages/Section/types";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import type { ArticleDataObject, Section } from "@typings/Cue";
import { Type } from "@typings/enums";
import { cn } from "@util/helpers";

type EventsAwardsNewsListingStoriesProps = {
  article: ArticleDataObject;
  index: number;
  awardType: string;
};

/**
 * Component to render news listing page for event and awards
 */
const EventsAwardsNewsListingStories = ({
  article: {
    media,
    kicker,
    sections,
    id,
    title,
    displaySetting,
    tags,
    created,
    blurb,
    urlPath,
    paywall,
    edited,
    updated,
  },
  index,
}: EventsAwardsNewsListingStoriesProps): React.ReactElement => {
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);
  const { isScreenSM } = useWindowSize();
  const { pathname } = useLocation();

  const isSubscriber = OKTAUserInfo?.usertype === OKTAUserTypeEnum.SUBSCRIBER;
  const sectionName = pathname.substring(pathname.lastIndexOf("/") + 1);
  const inPodcastsLandingPage = sectionName === "podcasts";
  const isLeadArticle = index === 0 && !inPodcastsLandingPage;
  const firstImage = media?.[0];
  const articleMedia = isLeadArticle && !isScreenSM ? undefined : firstImage;
  const isVideo =
    media?.[0]?.content?.type === Type.ArticleBrightcoveVideo ||
    media?.[0]?.content?.type === Type.ArticleYoutubeVideo;
  let section: Section | undefined;
  const pathArray = pathname.split("/");
  const uniqueName = sections?.[0]?.uniqueName || sectionName;

  if (pathArray.length > 1 && pathArray[1] === "keywords") {
    section = sections?.[0];
  }

  const variant: CardVariations = {
    kicker: {
      color: cn("text-gray-515"),
    },
    image: {
      width: "w-2/5 lg:w-1/6",
      position: "left",
      extraClass: "lg:order-2",
      aspectRatio: "object-cover",
      filters: [
        {
          breakpoint: "(min-width: 992px)",
          w: 400,
          h: 267,
          dpr: 1,
          f: "webp",
        },
        {
          breakpoint: "(min-width: 768px)",
          w: 300,
          h: 200,
          dpr: 1,
          f: "webp",
        },
        { w: 300, h: 200, dpr: 1, f: "webp" },
      ],
      isVideo: isVideo,
    },
    content: {
      width: "w-3/5 lg:w-5/6",
      extraClass: cn("lg:order-1 pl-4 lg:pl-0 lg:pr-4"),
      blurb: {
        extraClass: cn("text-gray-850"),
      },
      updatedTimeExtraClass: "font-public-sans",
    },
    title: {
      size: "",
      color: "text-gray-850",
      extraClass: "font-public-sans",
    },
  };

  return (
    <div
      data-testdata-testid="events-awards-news-listing-stories-component"
      className={cn("story border-b border-gray-175 py-3")}
    >
      <BasicCard
        id={id}
        title={displaySetting?.displayHeadline || title}
        section={section}
        storyThread={tags?.find(({ type }) => type === TagType.StoryThread)}
        kicker={kicker?.fields?.[0]?.value}
        media={articleMedia}
        blurb={blurb}
        slug={urlPath}
        paywall={isSubscriber ? paywall?.contentAccess === "1" : false}
        variations={variant}
        created={created}
        edited={edited}
        updated={updated}
        hasUpdatedTimestampDisplay={displaySetting?.hasUpdatedTimeDisplayed}
        defaultImage={{
          directoryName: uniqueName,
        }}
        rootClassName="items-stretch"
      />
    </div>
  );
};

export default EventsAwardsNewsListingStories;
